import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PatientProvider } from './contexts/PatientContext';

import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/AuthPages';
import ProfilePage from './pages/ProfilePage';
import PatientPage from './pages/PatientPage';
import NewAnalysisPage from './pages/NewAnalysisPage';
import ResultPage from './pages/ResultPage';
import HistoryPage from './pages/HistoryPage';
import SharePage from './pages/SharePage';
import AccountPage from './pages/AccountPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';

// ─── Auth Guards ──────────────────────────────────────────────────────────────

function ProtectedRoute({ children }) {
  const { username } = useAuth();
  return username ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { username } = useAuth();
  return !username ? children : <Navigate to="/" replace />;
}

// ─── Responsive hook — updates on resize ─────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return isMobile;
}

// ─── Mobile Shell ─────────────────────────────────────────────────────────────
// Mobile  → full-screen fixed shell, pages scroll internally via PageLayout
// Desktop → no wrapper at all, pages render normally like a regular website

function MobileShell({ children }) {
  const isMobile = useIsMobile();

  if (!isMobile) {
    // ✅ Desktop: render pages completely as-is, zero interference
    return <>{children}</>;
  }

  // ✅ Mobile: full-screen app shell
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflowY: 'auto',              // ✅ allow vertical scroll
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch', // smooth momentum on iOS
      background: 'var(--white)',
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {children}
    </div>
  );
}

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <MobileShell>
      <Routes>
        {/* Public */}
        <Route path="/login"                 element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/signup"                element={<PublicRoute><SignupPage /></PublicRoute>} />
        <Route path="/forgot-password"       element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* Protected */}
        <Route path="/"         element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/patient"  element={<ProtectedRoute><PatientPage /></ProtectedRoute>} />
        <Route path="/analysis" element={<ProtectedRoute><NewAnalysisPage /></ProtectedRoute>} />
        <Route path="/result"   element={<ProtectedRoute><ResultPage /></ProtectedRoute>} />
        <Route path="/history"  element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        <Route path="/share"    element={<ProtectedRoute><SharePage /></ProtectedRoute>} />
        <Route path="/account"  element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
        <Route path="/about"    element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
        <Route path="/contact"  element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MobileShell>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PatientProvider>
          <AppRoutes />
        </PatientProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// ─── PageLayout helper ────────────────────────────────────────────────────────
// Use this ONLY in mobile-targeted pages for fixed header + scrollable
// content + fixed footer layout. On desktop, pages can use their own layout.
//
// Usage:
//   import { PageLayout } from '../App';
//
//   export default function MyPage() {
//     return (
//       <PageLayout header={<MyHeader />} footer={<BottomNav />}>
//         {/* scrollable content */}
//       </PageLayout>
//     );
//   }

export function PageLayout({ header, footer, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Fixed header */}
      {header && <div style={{ flexShrink: 0 }}>{header}</div>}

      {/* Scrollable content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}>
        {children}
      </div>

      {/* Fixed footer / bottom nav */}
      {footer && (
        <div style={{
          flexShrink: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          {footer}
        </div>
      )}

    </div>
  );
}